import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Select,
  Typography,
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MenuItem from '@material-ui/core/MenuItem';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getProviderCategoryList,
  getProviderListByCategoryName,
  updateProviderSequence,
} from '../../../api/cms/provider';
import Breadcrumbs from '../../../components/Breadcrumbs';
import ApplicationLayout from '../../../components/layouts/ApplicationLayout';
import { LoadingBox } from '../../../components/LoadingBox';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

const ProviderSequence = () => {
  const [provider, setProvider] = useState([]);
  const [providerCategoryCode, setProviderCategoryCode] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getProviderCategoryList();
        const categoryList = _.get(res, 'categories', []);
        if (categoryList.length > 0) {
          setCategories(categoryList);
          setProviderCategoryCode(categoryList[0].category_code); // auto-select first category
        }
      } catch (err) {
        toast.error('Failed to fetch categories');
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      if (!providerCategoryCode) return;
      try {
        const data = await getProviderListByCategoryName(providerCategoryCode);
        const filteredProviders = data
          .map((provider) => {
            const matchedAffiliate = provider.affiliate_settings?.find(
              (aff) => aff.category === providerCategoryCode,
            );
            if (matchedAffiliate) {
              return {
                ...provider,
                affiliate: matchedAffiliate,
              };
            }
            return null;
          })
          .filter(Boolean)
          .sort((a, b) => a.affiliate.order - b.affiliate.order);

        setProvider(filteredProviders);
      } catch (err) {
        toast.error('Failed to fetch providers');
      }
    };

    fetchProviders();
  }, [providerCategoryCode]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(provider);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setProvider(items);
  };

  const handleSaveSequence = async () => {
    if (!providerCategoryCode || provider.length === 0) return;

    const payload = {
      list_provider_sequence: provider.map((item, index) => ({
        provider_id: item.provider_id,
        category_code: providerCategoryCode,
        order: index + 1,
      })),
    };

    try {
      await updateProviderSequence(payload);
      toast.success('Provider sequence updated successfully!');
    } catch (err) {
      toast.error('Failed to update provider sequence');
    }
  };

  return (
    <ApplicationLayout>
      {!provider ? (
        <LoadingBox />
      ) : (
        <>
          <Breadcrumbs />
          <Typography variant="h6" gutterBottom>
            Provider Sequence
          </Typography>

          <Box mb={3}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item>
                <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={providerCategoryCode}
                    onChange={(e) => setProviderCategoryCode(e.target.value)}
                    label="Category"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.category_code} value={cat.category_code}>
                        {cat.category_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Typography component="div">
                  <b>
                    <i>** Drag and drop to rearrange sequence</i>
                  </b>
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box mb={3}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Logo</TableCell>
                    <TableCell>Provider Name</TableCell>
                    <TableCell>Provider Code</TableCell>
                    <TableCell>Sequence</TableCell>
                    <TableCell>Platform Enabled</TableCell>
                  </TableRow>
                </TableHead>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="ProviderSequence">
                    {(provided) => (
                      <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                        {provider.map((row, index) => (
                          <Draggable
                            key={row.provider_code}
                            draggableId={row.provider_code}
                            index={index}
                          >
                            {(provided) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicatorIcon />
                                </TableCell>
                                <TableCell>
                                  <img src={row.affiliate.logo} height={30} alt="logo" />
                                </TableCell>
                                <TableCell>{row.provider_name}</TableCell>
                                <TableCell>{row.provider_code}</TableCell>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{row.is_platform_enable ? 'Yes' : 'No'}</TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
              </Table>
            </TableContainer>
          </Box>

          {providerCategoryCode && provider.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" color="primary" onClick={handleSaveSequence}>
                Save
              </Button>
            </Box>
          )}
        </>
      )}
    </ApplicationLayout>
  );
};

export default ProviderSequence;
